import { useEffect, useState } from "react";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  useMediaQuery,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Popover,
  Icon
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";
import ArrowUpwardOutlinedIcon from "@mui/icons-material/ArrowUpwardOutlined";
import NavigateBeforeOutlinedIcon from "@mui/icons-material/NavigateBeforeOutlined";
import NavigateNextOutlinedIcon from "@mui/icons-material/NavigateNextOutlined";
import KeyboardVoiceRoundedIcon from "@mui/icons-material/KeyboardVoiceRounded";
import MicOffRoundedIcon from "@mui/icons-material/MicOffRounded";
import { mcx } from "globals/endpoints";
import VoicePlayAudio from "application/components/VoicePlayAudio";
import MDTypography from "components/MDTypography";
import CTLNotification from "application/components/CTLNotification";
import { useMaterialUIController } from "context";
import AuthenticationService from "application/modules/authentication/AuthenticationService";

const SORT_OPTIONS = [
  { value: "floorTakenEpoch", label: "Latest", type: "number" },
  { value: "designation", label: "Name", type: "string" },
];

const DEFAULT_PAGE_SIZE = 10;
const EXPANDED_PAGE_SIZE = 36;

const Attributes = {
  modes: [],
};

const emptyFallback = (value, fallback = "--") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }
  return value;
};

const departmentList = AuthenticationService.getDepartmentList();
const getDepartmentInfo = (conferenceNo) => {
  if (!conferenceNo) return null;
  if (!departmentList || !Array.isArray(departmentList)) return null;
  const matched = departmentList.find(
    (dept) => String(dept.extension) === String(conferenceNo),
  );
  return matched ? matched.department : "Private Call";
};

const formatEpochCompact = (value) => {
  if (value === null || value === undefined || value === "") {
    return "--";
  }

  const raw = Number(value);
  if (!Number.isFinite(raw)) {
    return "--";
  }

  const millis = value.toString().length === 10 ? raw * 1000 : raw;
  const date = new Date(millis);

  if (Number.isNaN(date.getTime())) {
    return "--";
  }

  const datePart = date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  const time = date.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  return `${datePart} ${time}`;
};

function ppdrReport({
    // ...existing code...
  onLoad,
  pageId,
  searchText,
  registerCallBacks,
  isLeftPanelExpanded = false,
  isCompactScreen = false,
}) {
  const theme = useTheme();
  const isSmallViewport = useMediaQuery(theme.breakpoints.down("md"));
  const [controller] = useMaterialUIController();
  const { darkMode, activePage } = controller;
  const isSmallView = isCompactScreen || isSmallViewport;
  const pageSize = isLeftPanelExpanded && !isSmallView
    ? EXPANDED_PAGE_SIZE
    : DEFAULT_PAGE_SIZE;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortBy, setSortBy] = useState("floorTakenEpoch");
  const [sortDirection, setSortDirection] = useState("desc");
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "PPDR CDR",
    status: "",
  });
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [tempFromDate, setTempFromDate] = useState("");
  const [tempToDate, setTempToDate] = useState("");
  const departmentList =  AuthenticationService.getDepartmentList();

  useEffect(() => {
    if (typeof onLoad === "function") {
      onLoad(Attributes);
    }

    if (typeof registerCallBacks === "function") {
      registerCallBacks({});
    }
  }, [activePage, onLoad, registerCallBacks]);

  useEffect(() => {
    const timerId = window.setTimeout(() => {
      setDebouncedSearch((searchText || "").trim());
      setCurrentPage(0);
    }, 350);
    return () => window.clearTimeout(timerId);
  }, [searchText]);

  useEffect(() => {
    setCurrentPage(0);
  }, [sortBy, sortDirection]);

  useEffect(() => {
    setCurrentPage(0);
  }, [pageSize]);

  useEffect(() => {
    let isMounted = true;

    const getData = async ({ silent = false } = {}) => {
      if (!silent && isMounted) {
        setLoading(true);
      }
      try {
        const payload = {
          currentPage,
          pageSize,
          sortBy,
          sortDirection,
          search: debouncedSearch,
          conferenceNumber: departmentList?.map((dept) => dept.extension).join(","),
        };

        if (fromDate) {
          payload.fromEpoch = new Date(fromDate).setHours(0, 0, 0, 0);
        }
        if (toDate) {
          payload.toEpoch = new Date(toDate).setHours(23, 59, 59, 999);
        }

        const response = await mcx.post("floor-control-history/listpage", payload);

        if (!isMounted) return;

        const pageData = response?.data?.data || {};
        const content = Array.isArray(pageData?.content)
          ? pageData.content
          : Array.isArray(pageData)
          ? pageData
          : [];

        setRows(content);

        const total = pageData?.totalRecords ?? pageData?.totalElements ?? content.length;
        setTotalRecords(total);
        setTotalPages(pageData?.totalPages ?? (total > 0 ? Math.ceil(total / pageSize) : 0));
      } catch (error) {
        if (!isMounted) return;
        setRows([]);
        setTotalRecords(0);
        setTotalPages(0);
        setNotify({
          isOpen: true,
          message: error?.response?.data?.message || "Failed to fetch PPDR CDR",
          type: "error",
          pagename: "PPDR CDR",
          status:
            error?.response?.data?.messageDetail ||
            error?.message ||
            "Unable to get floor-control history.",
        });
      } finally {
        if (isMounted && !silent) {
          setLoading(false);
        }
      }
    };

    getData();

    const interval = setInterval(() => {
      getData({ silent: true });
    }, 5000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [currentPage, pageSize, debouncedSearch, sortBy, sortDirection, fromDate, toDate, activePage]);

  const canGoPrevious = currentPage > 0;
  const canGoNext = totalPages > 0 && currentPage < totalPages - 1;

  const panelGradient = darkMode
    ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)"
    : "linear-gradient(135deg, #f5f5f5 0%, #e3f2fd 50%, #f0f4ff 100%)";

  return (
    <>
      <Card
        sx={{
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          borderRadius: 2,
          boxShadow: (theme) =>
            darkMode
              ? `0 4px 24px ${alpha(theme.palette.common.black, 0.4)}`
              : `0 2px 16px ${alpha(theme.palette.info.main, 0.07)}`,
          p: 0.85,
          gap: 0.7,
          background: (theme) =>
            darkMode
              ? `linear-gradient(160deg, ${alpha(theme.palette.background.default, 0.98)} 0%, ${alpha(theme.palette.info.dark, 0.08)} 100%)`
              : `linear-gradient(160deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.info.light, 0.06)} 100%)`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.6,
            flexWrap: "nowrap",
            overflowX: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <Chip
            label={`Total Records: ${totalRecords}`}
            size="small"
            color="info"
            variant="outlined"
            sx={{
              height: 18,
              fontSize: "0.58rem",
              flexShrink: 0,
              "& .MuiChip-label": { px: 0.8 },
            }}
          />

          <MDTypography
            variant="caption"
            sx={{
              fontSize: "0.63rem",
              fontWeight: 600,
              opacity: 0.7,
              flexShrink: 0,
            }}
          >
            Sort By
          </MDTypography>

          <Select
            size="small"
            value={sortBy}
            onChange={(event) => setSortBy(event.target.value)}
            sx={{
              flex: 1,
              minWidth: 0,
              maxWidth: 110,
              height: 26,
              fontSize: 11,
              borderRadius: 1.5,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: (theme) => alpha(theme.palette.info.main, 0.3),
              },
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                height: 26,
                py: 0,
                pr: 2.5,
                pl: 0.8,
              },
            }}
          >
            {SORT_OPTIONS.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                sx={{ fontSize: 12 }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>

          <Tooltip
            title={sortDirection === "desc" ? "Descending" : "Ascending"}
          >
            <IconButton
              size="small"
              onClick={() =>
                setSortDirection((previous) =>
                  previous === "desc" ? "asc" : "desc",
                )
              }
              sx={{
                width: 26,
                height: 26,
                flexShrink: 0,
                border: (theme) =>
                  `1px solid ${alpha(theme.palette.info.main, 0.35)}`,
                borderRadius: 1.5,
                transition: "all 0.2s",
                "&:hover": {
                  backgroundColor: (theme) => alpha(theme.palette.info.main, 0.12),
                  transform: "scale(1.1)",
                },
              }}
            >
              {sortDirection === "desc" ? (
                <ArrowDownwardOutlinedIcon sx={{ fontSize: 13 }} color="info" />
              ) : (
                <ArrowUpwardOutlinedIcon sx={{ fontSize: 13 }} color="info" />
              )}
            </IconButton>
          </Tooltip>

          {fromDate || toDate ? (
            <Tooltip title="Remove filter">
              <IconButton
                size="small"
                onClick={() => {
                  setFromDate("");
                  setToDate("");
                }}
                sx={{
                  width: 26,
                  height: 26,
                  flexShrink: 0,
                  border: (theme) =>
                    `1px solid ${alpha(theme.palette.error.main, 0.35)}`,
                  borderRadius: 1.5,
                  transition: "all 0.2s",
                  "&:hover": {
                    backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
                    transform: "scale(1.08)",
                  },
                }}
              >
                <Icon sx={{ color: "error.main", fontSize: 16 }}>clear</Icon>
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Filter by date">
              <IconButton
                size="small"
                onClick={(e) => {
                  setTempFromDate(fromDate);
                  setTempToDate(toDate);
                  setFilterAnchorEl(e.currentTarget);
                }}
                sx={{
                  width: 26,
                  height: 26,
                  flexShrink: 0,
                  border: (theme) =>
                    `1px solid ${alpha(theme.palette.success.main, 0.35)}`,
                  borderRadius: 1.5,
                  transition: "all 0.2s",
                  "&:hover": {
                    backgroundColor: (theme) => alpha(theme.palette.success.main, 0.1),
                    transform: "scale(1.08)",
                  },
                }}
              >
                <Icon sx={{ color: "success.main", fontSize: 16 }}>tune</Icon>
              </IconButton>
            </Tooltip>
          )}

          <MDTypography
            variant="caption"
            color="text"
            sx={{ opacity: 0.5, fontSize: "0.58rem", flexShrink: 0 }}
          >
            Auto refresh: 5s
          </MDTypography>

          {/* Date filter popover */}
          <Popover
            open={Boolean(filterAnchorEl)}
            anchorEl={filterAnchorEl}
            onClose={() => setFilterAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: {
                p: 1.5,
                minWidth: 230,
                borderRadius: 2,
                border: (theme) => `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
                boxShadow: (theme) =>
                  darkMode
                    ? `0 8px 24px ${alpha(theme.palette.common.black, 0.45)}`
                    : `0 8px 24px ${alpha(theme.palette.info.main, 0.12)}`,
                background: darkMode
                  ? "linear-gradient(135deg, #1c1a1a 0%, #1f1f20 50%, #19191a 100%)"
                  : "linear-gradient(135deg, #f5f5f5 0%, #e3f2fd 50%, #f0f4ff 100%)",
              },
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="From Date"
                type="date"
                size="small"
                value={tempFromDate}
                onChange={(e) => setTempFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="To Date"
                type="date"
                size="small"
                value={tempToDate}
                onChange={(e) => setTempToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Box
                sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
              >
                <Button
                  onClick={() => setFilterAnchorEl(null)}
                  color="inherit"
                  size="small"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    setFromDate(tempFromDate);
                    setToDate(tempToDate);
                    setFilterAnchorEl(null);
                  }}
                  color="primary"
                  variant="contained"
                  size="small"
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </Popover>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            borderRadius: 1.2,
            border: (theme) =>
              `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
            p: 0.55,
            overflowY: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          {loading && rows.length === 0 ? (
            <Box
              sx={{
                minHeight: 180,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <CircularProgress size={22} color="info" />
              <MDTypography
                variant="caption"
                color="text"
                sx={{ opacity: 0.72 }}
              >
                Loading PPDR CDR data...
              </MDTypography>
            </Box>
          ) : rows.length === 0 ? (
            <Box
              sx={{
                minHeight: 160,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MDTypography
                variant="caption"
                color="text"
                sx={{ opacity: 0.75 }}
              >
                No floor-control history found.
              </MDTypography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                gap: 0.55,
              }}
            >
              {rows.map((row) => (
                <Card
                  key={row?.id || row?.sessionId}
                  sx={{
                    border: (theme) =>
                      `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
                    borderRadius: 1,
                    px: 0.52,
                    py: 0.42,
                    minHeight: 65,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    gap: 0.14,
                   }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 0.35,
                    }}
                  >
                    <MDTypography
                      variant="caption"
                      fontWeight="bold"
                      sx={{
                        fontSize: "0.68rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {`${emptyFallback(row?.designation, "Unknown")}`}
                    </MDTypography>
 
                    <MDTypography
                      variant="caption"
                      sx={{ fontSize: "0.62rem", opacity: 0.82 }}
                    >
                      {`${getDepartmentInfo(row?.conferenceNo)}`}
                    </MDTypography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.25,
                        flexShrink: 0,
                      }}
                    >
                      <MDTypography
                        variant="caption"
                        color="text"
                        sx={{
                          fontSize: "0.6rem",
                          opacity: 0.95,
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          mr: 0.15,
                        }}
                      >
                        <Chip
                          label={row?.streamType == "video" ? "Video" : "Replay"}
                          size="extraExtraSmall"
                          sx={{
                            ml: 0.3,
                            height: 16,
                            fontSize: "0.5rem",
                            fontWeight: 500,
                            backgroundColor: row?.streamType === "video"
                              ? alpha(theme.palette.success.main, 0.15)
                              : alpha(theme.palette.info.main, 0.15),
                            color:
                              row?.streamType === "video"
                                ? theme.palette.success.main
                                : theme.palette.info.main,
                          }}
                        />
                      </MDTypography>

                      <VoicePlayAudio
                        value={row?.sessionId}
                        row={row}
                        compact
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      height: "1px",
                      mx: -0.52,
                      my: 0.15,
                      background: (theme) =>
                        `linear-gradient(90deg, transparent, ${alpha(theme.palette.divider, 0.6)}, transparent)`,
                    }}
                  />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 0.5,
                      flexWrap: "wrap",
                      width: "100%",
                    }}
                  >
                    <Tooltip
                      title={`PTT Press`}
                      placement="bottom"
                    >
                      <Chip
                        size="small"
                        icon={<KeyboardVoiceRoundedIcon sx={{ fontSize: 12 }} />}
                        label={`${formatEpochCompact(row?.floorTakenEpoch)}`}
                        sx={{
                          height: 18,
                          fontSize: "0.58rem",
                          fontWeight: 600,
                          bgcolor: (theme) => alpha(theme.palette.success.main, 0.14),
                          color: "success.main",
                          border: (theme) =>
                            `1px solid ${alpha(theme.palette.success.main, 0.34)}`,
                          "& .MuiChip-label": {
                            px: 0.7,
                          },
                          "& .MuiChip-icon": {
                            ml: 0.45,
                            mr: -0.2,
                            color: "success.main",
                          },
                        }}
                      />
                    </Tooltip>

                    <Tooltip
                      title={`PTT Release`}
                      placement="bottom"
                    >
                      <Chip
                        size="small"
                        icon={<MicOffRoundedIcon sx={{ fontSize: 12 }} />}
                        label={`${formatEpochCompact(row?.floorReleaseEpoch)}`}
                        sx={{
                          height: 18,
                          fontSize: "0.58rem",
                          fontWeight: 600,
                          bgcolor: (theme) => alpha(theme.palette.warning.main, 0.14),
                          color: "warning.main",
                          border: (theme) =>
                            `1px solid ${alpha(theme.palette.warning.main, 0.34)}`,
                          "& .MuiChip-label": {
                            px: 0.7,
                          },
                          "& .MuiChip-icon": {
                            ml: 0.45,
                            mr: -0.2,
                            color: "warning.main",
                          },
                        }}
                      />
                    </Tooltip>
                  </Box>
                </Card>
              ))}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.5,
            pt: 0.2,
            borderTop: (theme) => `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          }}
        >
          <MDTypography variant="caption" color="text" sx={{ opacity: 0.6, fontSize: "0.6rem" }}>
            Page {totalPages === 0 ? 0 : currentPage + 1} / {totalPages}
          </MDTypography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
            <IconButton
              size="small"
              color="text"
              disabled={!canGoPrevious}
              onClick={() =>
                setCurrentPage((previous) => Math.max(previous - 1, 0))
              }
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                border: (theme) =>
                  `1px solid ${alpha(theme.palette.info.main, canGoPrevious ? 0.4 : 0.15)}`,
                transition: "all 0.18s",
                "&:not(:disabled):hover": {
                  backgroundColor: (theme) => alpha(theme.palette.info.main, 0.12),
                  transform: "scale(1.1)",
                },
              }}
            >
              <NavigateBeforeOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>

            <IconButton
              size="small"
              color="text"
              disabled={!canGoNext}
              onClick={() =>
                setCurrentPage((previous) =>
                  totalPages > 0
                    ? Math.min(previous + 1, totalPages - 1)
                    : previous,
                )
              }
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                border: (theme) =>
                  `1px solid ${alpha(theme.palette.info.main, canGoNext ? 0.4 : 0.15)}`,
                transition: "all 0.18s",
                "&:not(:disabled):hover": {
                  backgroundColor: (theme) => alpha(theme.palette.info.main, 0.12),
                  transform: "scale(1.1)",
                },
              }}
            >
              <NavigateNextOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        </Box>
      </Card>

      <CTLNotification notify={notify} setNotify={setNotify} />
    </>
  );
}

export default ppdrReport;
